import { useCallback } from 'react';
import { FiMapPin } from 'react-icons/fi';
import TypeaheadDropdown, { TypeaheadDataType } from '../TypeaheadDropdown';
import { FIELD_HINT_CLASS, FIELD_LABEL_CLASS, INPUT_CLASS } from '../../../pages/Onboarding/OnboardingUI';
import { searchCities, selectCity } from '../../../utils/cityLookup';
import { CityPlace } from '../../../utils/cityPlace';

interface CityLookupFieldProps {
    id: string;
    label?: string;
    hint?: string;
    /** `inline` sits the hint on the label row. `below` keeps it under the input. */
    hintPlacement?: 'below' | 'inline';
    placeholder?: string;
    value: string;
    /** Active search text. Empty once a suggestion is committed, so the menu closes. */
    query: string;
    error?: string;
    menuPlacement?: 'above' | 'below';
    onTextChange: (value: string) => void;
    onSelect: (place: CityPlace) => void;
    onBlur?: () => void;
    search?: (query: string, signal?: AbortSignal) => Promise<TypeaheadDataType[]>;
}

const MENU_CLASS = 'bg-white border border-gray-200 rounded-xl shadow-lg p-1 max-h-[180px] overflow-y-auto';
const ITEM_CLASS = 'px-3 py-2 rounded-lg text-sm text-gray-700 hover:bg-purple-light/10 hover:text-purple-light';

function CityLookupField({
    id,
    label = 'City',
    hint,
    hintPlacement = 'below',
    placeholder = 'Start typing your city',
    value,
    query,
    error,
    menuPlacement = 'below',
    onTextChange,
    onSelect,
    onBlur,
    search,
}: CityLookupFieldProps) {
    const loadCities = useCallback((
        nextQuery: string,
        _page?: number,
        _limit?: number,
        signal?: AbortSignal,
    ) => {
        if (search) return search(nextQuery, signal);
        return searchCities(nextQuery, { signal });
    }, [search]);

    return (
        <div>
            {label ? (
                hint && hintPlacement === 'inline' ? (
                    <div className="mb-2 flex flex-wrap items-baseline gap-x-2">
                        <label className={`${FIELD_LABEL_CLASS} !mb-0 shrink-0`} htmlFor={id}>
                            {label}
                        </label>
                        <span id={`${id}-hint`} className="min-w-0 text-xs font-normal normal-case tracking-normal text-gray-400">
                            {hint}
                        </span>
                    </div>
                ) : (
                    <label className={FIELD_LABEL_CLASS} htmlFor={id}>
                        {label}
                    </label>
                )
            ) : null}
            <div className="relative">
                <FiMapPin className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" aria-hidden />
                <input
                    id={id}
                    type="text"
                    autoComplete="off"
                    autoCorrect="off"
                    spellCheck={false}
                    placeholder={placeholder}
                    value={value}
                    aria-invalid={error ? true : undefined}
                    aria-describedby={error ? `${id}-note` : hint ? `${id}-hint` : undefined}
                    onChange={(event) => onTextChange(event.target.value)}
                    onBlur={onBlur}
                    className={INPUT_CLASS}
                />
                <TypeaheadDropdown
                    wrapperClass={menuPlacement === 'above' ? 'bottom-full mb-1' : 'top-full mt-1'}
                    containerClassName={MENU_CLASS}
                    itemClassName={ITEM_CLASS}
                    query={query}
                    getData={loadCities}
                    itemClick={(place) => {
                        void selectCity(place).then(onSelect);
                    }}
                />
            </div>
            {error ? (
                <p id={`${id}-note`} role="alert" className="mt-1.5 text-sm text-red-500">{error}</p>
            ) : hint && hintPlacement === 'below' ? (
                <p id={`${id}-hint`} className={FIELD_HINT_CLASS}>{hint}</p>
            ) : null}
        </div>
    );
}

export default CityLookupField;
